import React, { useEffect, useState } from "react";
import Layout from "../../components/Sidebar";
import api from "../../api";
import { Users, Clock, CalendarDays, CheckSquare, TrendingUp, AlertCircle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const StatCard = ({ icon: Icon, label, value, color, bg }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex items-start gap-4 stat-card-hover">
    <div className={`p-3 rounded-xl ${bg} shrink-0`}>
      <Icon size={22} className={color} />
    </div>
    <div>
      <p className="text-slate-500 text-sm font-medium">{label}</p>
      <p className="text-3xl font-bold text-[#1a2332] mt-1" data-testid={`stat-${label.toLowerCase().replace(/\s+/g,"-")}`}>{value}</p>
    </div>
  </div>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/reports/overview"), api.get("/leaves")])
      .then(([rep, lv]) => {
        setData(rep.data);
        setLeaves(lv.data.filter((l) => l.status === "pending").slice(0, 5));
      })
      .finally(() => setLoading(false));
  }, []);

  const stats = data
    ? [
        { icon: Users, label: "Total Employees", value: data.overview.total_employees, color: "text-[#38b6e8]", bg: "bg-sky-50" },
        { icon: Clock, label: "Present Today", value: data.overview.today_attendance, color: "text-emerald-600", bg: "bg-emerald-50" },
        { icon: CalendarDays, label: "Pending Leaves", value: data.overview.pending_leaves, color: "text-amber-600", bg: "bg-amber-50" },
        { icon: CheckSquare, label: "Active Tasks", value: data.overview.active_tasks, color: "text-violet-600", bg: "bg-violet-50" },
      ]
    : [];

  return (
    <Layout>
      <div className="p-8 page-enter">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">
            {new Date().toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 h-28 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="stats-grid">
            {stats.map((s) => <StatCard key={s.label} {...s} />)}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Attendance trend */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp size={18} className="text-[#38b6e8]" />
              <h3 className="font-semibold text-slate-900">Attendance — Last 7 Days</h3>
            </div>
            {data?.attendance_trend && (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.attendance_trend} barSize={28}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false}
                    tickFormatter={(v) => new Date(v).toLocaleDateString("en-IN", { weekday: "short" })} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                    formatter={(v) => [`${v} present`, "Attendance"]}
                    labelFormatter={(v) => new Date(v).toLocaleDateString("en-IN", { month: "short", day: "numeric" })}
                  />
                  <Bar dataKey="present" fill="#38b6e8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Pending leaves */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-5">
              <AlertCircle size={18} className="text-amber-500" />
              <h3 className="font-semibold text-slate-900">Pending Leaves</h3>
              {leaves.length > 0 && (
                <span className="ml-auto bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full">{leaves.length}</span>
              )}
            </div>
            {leaves.length === 0 ? (
              <div className="text-center py-8">
                <CalendarDays size={32} className="text-slate-300 mx-auto mb-2" />
                <p className="text-slate-400 text-sm">No pending requests</p>
              </div>
            ) : (
              <div className="space-y-3">
                {leaves.map((l) => (
                  <div key={l.id} className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <p className="font-semibold text-slate-800 text-sm">{l.employee_name}</p>
                    <p className="text-slate-500 text-xs mt-0.5">
                      {l.start_date} → {l.end_date}
                    </p>
                    <p className="text-slate-600 text-xs mt-1 truncate">{l.reason}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
